import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Wallet } from 'lucide-react';
import { Field, NumInput, idr, today } from '@/components/erp/Erp';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const TYPES = { asset: 'Aset', liability: 'Liabilitas', equity: 'Ekuitas', income: 'Pendapatan', expense: 'Beban' };
const ORDER = ['asset', 'liability', 'equity', 'income', 'expense'];

// Saldo awal per akun (termasuk kas/bank). Nilai = saldo normal akun; selisih otomatis ke 3900 Ekuitas Saldo Awal.
export const OpeningBalanceDialog = ({ open, onClose, call, accounts, onSaved, id }) => {
  const [vals, setVals] = useState({});
  const [date, setDate] = useState(today());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    call('get', '/accounting/opening-balance').then((d) => {
      const v = {};
      (d.lines || []).forEach((l) => { const a = accounts.find((x) => x.id === l.account_id); if (!a || a.code === '3900') return; v[l.account_id] = ['asset', 'expense'].includes(a.type) ? l.debit - l.credit : l.credit - l.debit; });
      setVals(v); if (d.date) setDate(d.date);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [open, call, accounts]);
  const list = accounts.filter((a) => a.is_active !== false && a.code !== '3900' && (!q || `${a.code} ${a.name}`.toLowerCase().includes(q.toLowerCase())));
  const sum = (t) => accounts.filter((a) => a.type === t).reduce((s, a) => s + Number(vals[a.id] || 0), 0);
  const debit = sum('asset') + sum('expense'); const credit = sum('liability') + sum('equity') + sum('income');
  const diff = debit - credit;
  const save = async () => {
    setBusy(true);
    try {
      const lines = Object.entries(vals).filter(([, v]) => Number(v)).map(([account_id, v]) => ({ account_id, balance: Number(v) }));
      const r = await call('put', '/accounting/opening-balance', { date, lines });
      toast.success(id ? `Saldo awal tersimpan${r.balancing ? ` · penyeimbang ${idr(Math.abs(r.balancing))} ke 3900 Ekuitas Saldo Awal` : ''}` : 'Opening balance saved');
      onSaved(); onClose();
    } catch (e) { /* toast */ } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="opening-balance-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Wallet size={18} className="text-primary" />{id ? 'Saldo awal akun' : 'Opening balances'}</DialogTitle>
          <DialogDescription>{id ? 'Isi saldo awal kas, bank, piutang, persediaan, utang, modal, dll. saat mulai memakai Pillot. Selisih debit/kredit otomatis diseimbangkan ke akun 3900 Ekuitas Saldo Awal. Menyimpan ulang akan mengganti saldo awal sebelumnya.' : 'Enter opening balances for cash, bank, receivables, inventory, payables, equity. Any difference is balanced to 3900 Opening Balance Equity.'}</DialogDescription></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-3">
          <Field label={id ? 'Tanggal saldo awal' : 'Opening date'}><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl" data-testid="opening-date" /></Field>
          <Field label={id ? 'Cari akun' : 'Search account'}><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={id ? 'Kas, Bank, Piutang…' : 'Cash, Bank…'} className="rounded-xl" data-testid="opening-search" /></Field>
        </div>
        {loading ? <div className="py-8 text-center"><Loader2 className="animate-spin inline" /></div> : (
          <div className="rounded-xl border border-border overflow-hidden max-h-[46vh] overflow-y-auto">
            <table className="w-full text-sm">
              <tbody>
                {ORDER.map((t) => { const rows = list.filter((a) => a.type === t); if (!rows.length) return null; return (
                  <React.Fragment key={t}>
                    <tr className="bg-muted/60"><td colSpan={2} className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{TYPES[t]}</td><td className="px-3 py-1.5 text-right text-xs font-semibold tabular-nums">{idr(sum(t))}</td></tr>
                    {rows.map((a) => (
                      <tr key={a.id} className="border-t border-border/60" data-testid={`opening-row-${a.code}`}>
                        <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground w-16">{a.code}</td>
                        <td className="px-3 py-1.5">{a.name}</td>
                        <td className="px-3 py-1 w-44"><NumInput value={vals[a.id] ?? ''} onChange={(e) => setVals({ ...vals, [a.id]: e.target.value })} className="h-8 text-right" placeholder="0" data-testid={`opening-input-${a.code}`} /></td>
                      </tr>
                    ))}
                  </React.Fragment>
                ); })}
              </tbody>
            </table>
          </div>
        )}
        <div className={`rounded-xl p-3 text-sm flex flex-wrap justify-between gap-2 ${diff === 0 ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`} data-testid="opening-summary">
          <span>Debit {idr(debit)}</span><span>Kredit {idr(credit)}</span>
          <span>{diff === 0 ? (id ? 'Seimbang' : 'Balanced') : (id ? `Selisih ${idr(Math.abs(diff))} → 3900 Ekuitas Saldo Awal` : `Difference ${idr(Math.abs(diff))} → 3900`)}</span>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" className="rounded-xl" onClick={onClose}>{id ? 'Batal' : 'Cancel'}</Button>
          <Button type="button" className="rounded-xl" disabled={busy || loading} onClick={save} data-testid="opening-save" data-perm-admin>{busy ? <Loader2 size={16} className="animate-spin" /> : (id ? 'Simpan saldo awal' : 'Save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
